import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@services/auth';
import { useSearchHighlight } from '../shared/useSearchHighlight';
import { apiGet, apiPut } from '@services/api';
import { getImageUrl } from '@utils/imageUrl';
import { normalizeStoredImageRef } from '@utils/profilePicture';
import { SKILLS_WITH_DESCRIPTIONS } from '@data/mockOfferFeatures';
import {
	getMyCompany,
	getMyCompanyMembers,
	getMyCompanyContexts,
	setMyActiveCompany,
	notifyCompanyWorkspaceChanged,
	listCompanies,
	requestJoinCompany,
	approveCompanyMember,
	rejectCompanyMember,
	acceptCompanyInvite,
	declineCompanyInvite,
	leaveCompany,
	createCompany,
	inviteToCompany,
	updateMyCompanyProfile,
	getCompanySchedule,
	updateCompanySchedule,
	type MyCompanyResponse,
	type Company,
	type CompanyContextsResponse,
	type CompanyNonWorkingDate,
} from '@services/companies';
import { createGardenerInvite } from '@services/gardenerInvites';
import { getUsers, getMyTimeOff, addTimeOff, deleteTimeOff, deleteMyAccount, type UserResponse, type UserTimeOffEntry } from '@services/users';
import { useI18n } from '../i18n';
import { buildAppUrl, getApiBaseUrl } from '@utils/appBase';

const API_BASE_URL = getApiBaseUrl();
const MAX_PROFILE_IMAGE_BYTES = 5 * 1024 * 1024;
const COUNTRY_OPTIONS = [
	{ code: 'DE', label: 'Germany' },
	{ code: 'AT', label: 'Austria' },
	{ code: 'CH', label: 'Switzerland' },
	{ code: 'HR', label: 'Croatia' },
	{ code: 'US', label: 'United States' },
	{ code: 'GB', label: 'United Kingdom' },
	{ code: 'FR', label: 'France' },
	{ code: 'IT', label: 'Italy' },
	{ code: 'ES', label: 'Spain' },
	{ code: 'NL', label: 'Netherlands' },
];

function isHeicLike(file: File): boolean {
	const mime = (file.type || '').toLowerCase();
	const name = (file.name || '').toLowerCase();
	return mime.includes('heic') || mime.includes('heif') || name.endsWith('.heic') || name.endsWith('.heif');
}

async function convertHeicToJpeg(file: File): Promise<File> {
	const heic2anyModule = await import('heic2any');
	const heic2any = heic2anyModule.default as (options: { blob: Blob; toType: string; quality?: number }) => Promise<Blob | Blob[]>;
	const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
	const convertedBlob = Array.isArray(converted) ? converted[0] : converted;
	if (!convertedBlob) throw new Error('HEIC conversion returned no image');
	const baseName = (file.name || 'profile').replace(/\.[^.]+$/, '');
	return new File([convertedBlob], `${baseName}.jpg`, { type: 'image/jpeg' });
}

function openWhatsAppShare(text: string) {
	if (!text.trim()) return;
	const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
	window.open(url, '_blank', 'noopener,noreferrer');
}

export default function Settings() {
	const { user, logout, refreshUser } = useAuth();
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const getSkillLabel = (key: string, fallback: string) => {
		const map: Record<string, string> = {
			hardscaping: copy('Hardscaping', 'Bauarbeiten', 'Građevinski radovi'),
			planting: copy('Planting', 'Pflanzung', 'Sadnja'),
			irrigation: copy('Irrigation', 'Bewässerung', 'Navodnjavanje'),
			pruning: copy('Pruning', 'Rückschnitt', 'Rezidba'),
			lawn_care: copy('Lawn care', 'Rasenpflege', 'Njega travnjaka'),
			design: copy('Design', 'Design', 'Dizajn'),
			maintenance: copy('Maintenance', 'Pflege', 'Održavanje'),
			project_management: copy('Project management', 'Projektmanagement', 'Upravljanje projektom'),
			sustainable_gardening: copy('Sustainable gardening', 'Nachhaltiges Gärtnern', 'Održivo vrtlarstvo'),
			fencing_structures: copy('Fencing & structures', 'Zäune und Konstruktionen', 'Ograde i konstrukcije'),
			water_features: copy('Water features', 'Wasser-Elemente', 'Vodeni elementi'),
			lighting: copy('Lighting', 'Beleuchtung', 'Rasvjeta'),
			drainage: copy('Drainage', 'Entwässerung', 'Odvodnja')
		};
		return map[key] ?? fallback;
	};
	const getSkillDescription = (key: string, fallback: string) => {
		const map: Record<string, string> = {
			hardscaping: copy('Ground construction: excavation, sub-base, concrete, paving, porcelain/stone paths, patios, steps and retaining walls.', 'Bodenbau: Aushub, Unterbau, Beton, Pflaster, Porzellan-/Steinwege, Terrassen, Stufen und Stützmauern.', 'Građevinski radovi na tlu: iskopi, podloga, beton, opločnici, staze od kamena/porculana, terase, stepenice i potporni zidovi.'),
			planting: copy('Bed prep and planting of trees, shrubs, perennials, groundcovers, seasonal colour and mixed borders.', 'Beetvorbereitung und Pflanzung von Bäumen, Sträuchern, Stauden, Bodendeckern, saisonaler Bepflanzung und gemischten Beeten.', 'Priprema gredica i sadnja drveća, grmlja, trajnica, pokrivača tla, sezonskog cvijeća i miješanih gredica.'),
			irrigation: copy('Design and installation of drip lines, sprinklers, soaker hoses and irrigation controllers.', 'Planung und Installation von Tropfbewässerung, Regnern, Sickerschläuchen und Bewässerungssteuerungen.', 'Projektiranje i ugradnja sustava kap po kap, prskalica, crijeva i kontrolera navodnjavanja.'),
			pruning: copy('Structural and seasonal pruning of trees, shrubs and hedges for health, safety and shape.', 'Struktur- und saisonaler Schnitt von Bäumen, Sträuchern und Hecken für Gesundheit, Sicherheit und Form.', 'Strukturna i sezonska rezidba drveća, grmlja i živica za zdravlje, sigurnost i oblik.'),
			lawn_care: copy('Turf laying, repair and renovation, seeding, mowing, scarifying and lawn feeding.', 'Rasenverlegung, Reparatur und Renovierung, Aussaat, Mähen, Vertikutieren und Düngung.', 'Postavljanje travnjaka, popravci i obnova, sijanje, košnja, prozračivanje i prihrana.'),
			design: copy('Garden and landscape design: layout, planting plans, material choices and overall look & feel.', 'Garten- und Landschaftsdesign: Grundriss, Pflanzpläne, Materialauswahl und Gesamteindruck.', 'Dizajn vrta i krajolika: raspored, plan sadnje, odabir materijala i ukupni izgled.'),
			maintenance: copy('Regular garden care including weeding, mulching, deadheading, tidying and general upkeep.', 'Regelmäßige Gartenpflege einschließlich Jäten, Mulchen, Entfernen verwelkter Blüten, Aufräumen und allgemeiner Pflege.', 'Redovita njega vrta uključujući plijevljenje, malčiranje, uklanjanje ocvalog cvijeća, pospremanje i opće održavanje.'),
			project_management: copy('Planning and coordinating projects, timelines, trades, suppliers and client communication.', 'Planung und Koordination von Projekten, Zeitplänen, Gewerken, Lieferanten und der Kommunikation mit dem Kunden.', 'Planiranje i koordinacija projekata, rokova, izvođača, dobavljača i komunikacije s klijentom.'),
			sustainable_gardening: copy('Eco-friendly practices: composting, soil health, water-wise design and use of native / climate-suitable plants.', 'Nachhaltige Praktiken: Kompostierung, Bodengesundheit, wassersparende Planung und die Verwendung heimischer bzw. klimatisch geeigneter Pflanzen.', 'Ekološke prakse: kompostiranje, zdravlje tla, štedljivo korištenje vode i uporaba autohtonih / klimatski prikladnih biljaka.'),
			fencing_structures: copy('Building and installing fences, screens, pergolas, decks, benches, raised beds, outdoor kitchens and other timber or metal garden structures.', 'Bau und Installation von Zäunen, Sichtschutz, Pergolen, Terrassen, Bänken, Hochbeeten, Außenküchen und anderen Gartenkonstruktionen aus Holz oder Metall.', 'Izrada i ugradnja ograda, paravana, pergola, terasa, klupa, povišenih gredica, vanjskih kuhinja i drugih drvenih ili metalnih vrtnih konstrukcija.'),
			water_features: copy('Design and installation of ornamental ponds, fountains, cascades and other decorative water elements.', 'Planung und Installation von Zier­teichen, Brunnen, Kaskaden und anderen dekorativen Wasserelementen.', 'Projektiranje i ugradnja ukrasnih jezerca, fontana, kaskada i drugih dekorativnih vodenih elemenata.'),
			lighting: copy('Low-voltage outdoor lighting: path lights, spotlights, deck and feature lighting, including basic cabling and transformers.', 'Niedervolt-Außenbeleuchtung: Wegleuchten, Strahler sowie Terrassen- und Akzentbeleuchtung einschließlich grundlegender Verkabelung und Transformatoren.', 'Niskonaponska vanjska rasvjeta: rasvjeta staza, reflektori, rasvjeta terasa i akcentna rasvjeta, uključujući osnovno ožičenje i transformatore.'),
			drainage: copy('Managing surface and ground water: grading, soakaways, French drains and drainage around hard surfaces and structures.', 'Ableitung von Oberflächen- und Grundwasser: Gefälleausbildung, Sickerzonen, französische Drainagen und Entwässerung rund um befestigte Flächen und Bauwerke.', 'Upravljanje površinskom i podzemnom vodom: niveliranje, upojne zone, francuska drenaža i odvodnja oko tvrdih površina i konstrukcija.')
		};
		return map[key] ?? fallback;
	};
	const navigate = useNavigate();
	const fileInputRef = useRef<HTMLInputElement>(null);
	
	// Profile state
	const [profilePicture, setProfilePicture] = useState<string | null>(null);
	const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null); // Preview of newly selected file
	const [selectedProfilePictureFile, setSelectedProfilePictureFile] = useState<File | null>(null); // File to upload on save
	const [bio, setBio] = useState('');
	const [location, setLocation] = useState('');
	const [phone, setPhone] = useState('');
	const [countryCode, setCountryCode] = useState('');
	const [skills, setSkills] = useState<string[]>([]);
	
	// Settings state
	const [emailNotifications, setEmailNotifications] = useState(true);
	const [smsNotifications, setSmsNotifications] = useState(false);
	const [darkMode, setDarkMode] = useState(false);
	
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

	// Company (gardener / company role)
	const [companyData, setCompanyData] = useState<MyCompanyResponse | null>(null);
	const [membershipContexts, setMembershipContexts] = useState<CompanyContextsResponse | null>(null);
	const [switchingCompany, setSwitchingCompany] = useState(false);
	const [companiesList, setCompaniesList] = useState<Company[]>([]);
	const [selectedCompanyId, setSelectedCompanyId] = useState<number | ''>('');
	const [companyActionLoading, setCompanyActionLoading] = useState(false);
	const [newCompanyName, setNewCompanyName] = useState('');
	const [companyCountryCode, setCompanyCountryCode] = useState('');
	// Invite gardener (company only)
	const [inviteGardeners, setInviteGardeners] = useState<UserResponse[]>([]);
	const [inviteMemberIds, setInviteMemberIds] = useState<Set<number>>(new Set());
	const [inviteSelectedId, setInviteSelectedId] = useState<number | ''>('');
	const [inviteSearch, setInviteSearch] = useState('');
	const [inviteMode, setInviteMode] = useState<'existing' | 'new'>('existing');
	const [createdInviteCode, setCreatedInviteCode] = useState<string | null>(null);
	const [createdInviteLink, setCreatedInviteLink] = useState<string | null>(null);
	const [inviteActionMessage, setInviteActionMessage] = useState<string | null>(null);
	const [inviteLoading, setInviteLoading] = useState(false);
	const [inviteError, setInviteError] = useState<string | null>(null);
	// Company working days & non-working dates (company role only)
	const [workingDays, setWorkingDays] = useState<number[]>([1, 2, 3, 4, 5]);
	const [nonWorkingDates, setNonWorkingDates] = useState<CompanyNonWorkingDate[]>([]);
	const [newClosedDate, setNewClosedDate] = useState('');
	const [newClosedLabel, setNewClosedLabel] = useState('');
	const [scheduleSaving, setScheduleSaving] = useState(false);
	// My time off (PTO) - gardener and company
	const [timeOffEntries, setTimeOffEntries] = useState<UserTimeOffEntry[]>([]);
	const [ptoStartDate, setPtoStartDate] = useState('');
	const [ptoEndDate, setPtoEndDate] = useState('');
	const [ptoLabel, setPtoLabel] = useState('');
	const [ptoLoading, setPtoLoading] = useState(false);
	
	useSearchHighlight(); // Handle search highlighting

	// Handle hash navigation to subscription section
	useEffect(() => {
		if (window.location.hash === '#subscription') {
			setTimeout(() => {
				const element = document.querySelector('[data-section-id="subscription"]');
				if (element) {
					element.scrollIntoView({ behavior: 'smooth', block: 'start' });
					// Add highlight class temporarily
					element.classList.add('search-highlight');
					setTimeout(() => {
						element.classList.remove('search-highlight');
					}, 2000);
				}
			}, 100);
		}
	}, []);

	// Load profile and settings on mount
	useEffect(() => {
		if (!user?.id) return;
		
		const loadData = async () => {
			setLoading(true);
			try {
				// Load profile
				const profileData = await apiGet<{ profile: any }>(`/users/me/profile?userId=${user.id}`);
				if (profileData.profile) {
					setProfilePicture(normalizeStoredImageRef(profileData.profile.profile_picture_url));
					setBio(profileData.profile.bio || '');
					setLocation(profileData.profile.location || '');
					setPhone(profileData.profile.phone || '');
					setCountryCode((profileData.profile.country_code || '').toUpperCase());
					setSkills(Array.isArray(profileData.profile.skills) ? profileData.profile.skills : []);
				}
				
				// Load settings
				const settingsData = await apiGet<{ settings: any }>(`/users/me/settings?userId=${user.id}`);
				if (settingsData.settings) {
					setEmailNotifications(settingsData.settings.email_notifications ?? true);
					setSmsNotifications(settingsData.settings.sms_notifications ?? false);
					setDarkMode(settingsData.settings.dark_mode ?? false);
				}

				// Load company data for gardener/company (honours active company from auth)
				if (user.role === 'gardener' || user.role === 'company') {
					try {
						const activeCo =
							user.activeCompanyId === undefined || user.activeCompanyId === null
								? undefined
								: user.activeCompanyId;
						const [myCompany, companies, ctxRes] = await Promise.all([
							getMyCompany(user.id, activeCo),
							listCompanies(),
							getMyCompanyContexts(user.id),
						]);
						setCompanyData(myCompany);
						setMembershipContexts(ctxRes);
						setCompaniesList(companies);
						// Sync company schedule into local state
						if (myCompany?.company) {
							const wd = myCompany.company.working_days;
							setWorkingDays(Array.isArray(wd) && wd.length > 0 ? wd : [1, 2, 3, 4, 5]);
							setNonWorkingDates(Array.isArray(myCompany.company.non_working_dates) ? myCompany.company.non_working_dates : []);
							setCompanyCountryCode((myCompany.company.country_code || '').toUpperCase());
						}
						// For company: load gardeners and member ids for invite dropdown
						if (user.role === 'company' && myCompany?.company) {
							try {
								const [gardeners, membersRes] = await Promise.all([
									getUsers({ role: 'gardener' }),
									getMyCompanyMembers(user.id, myCompany.company.id),
								]);
								setInviteGardeners(gardeners);
								setInviteMemberIds(new Set((membersRes.members || []).map((m) => m.id)));
							} catch (_e) {}
						}
					} catch (e) {
						console.warn('Company data not available:', e);
					}
				}

				// Load my time off (PTO) for gardener and company
				if (user.role === 'gardener' || user.role === 'company') {
					try {
						const list = await getMyTimeOff(user.id);
						setTimeOffEntries(Array.isArray(list) ? list : []);
					} catch (e) {
						console.warn('Time off not available:', e);
					}
				}
				
			} catch (error) {
				console.error('Error loading profile/settings:', error);
				setMessage({ type: 'error', text: 'Failed to load profile and settings' });
			} finally {
				setLoading(false);
			}
		};
		
		loadData();
	}, [user?.id, user?.role, user?.activeCompanyId]);

	if (!user) {
		return null;
	}

	const handleLogout = () => {
		logout();
		navigate('/', { replace: true });
	};

	const handleDeleteAccount = async () => {
		if (!user?.id) return;
		const firstConfirm = window.confirm(
			copy(
				'Delete your profile? This will deactivate your account.',
				'Soll Ihr Profil gelöscht werden? Ihr Konto wird deaktiviert.',
				'Želite izbrisati profil? Vaš račun će biti deaktiviran.'
			)
		);
		if (!firstConfirm) return;
		const confirmEmail = window.prompt(
			copy(
				`Type your email (${user.email}) to confirm account deletion.`,
				`Geben Sie Ihre E-Mail (${user.email}) ein, um die Löschung zu bestätigen.`,
				`Upišite svoju e-poštu (${user.email}) za potvrdu brisanja računa.`
			),
			''
		);
		if (!confirmEmail) return;

		setSaving(true);
		setMessage(null);
		try {
			await deleteMyAccount(user.id, confirmEmail.trim());
			logout();
			navigate('/', { replace: true });
		} catch (error: any) {
			setMessage({
				type: 'error',
				text: error?.message || copy('Failed to delete account', 'Konto konnte nicht gelöscht werden', 'Brisanje računa nije uspjelo')
			});
		} finally {
			setSaving(false);
		}
	};

	const handleProfilePictureChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;
		event.target.value = '';

		// Validate file type
		if (!file.type.startsWith('image/')) {
			setMessage({ type: 'error', text: 'Please select an image file' });
			return;
		}

		// Validate file size (5MB)
		if (file.size > MAX_PROFILE_IMAGE_BYTES) {
			setMessage({ type: 'error', text: 'Image size must be less than 5MB' });
			return;
		}

		let fileForUpload = file;
		if (isHeicLike(file)) {
			try {
				fileForUpload = await convertHeicToJpeg(file);
			} catch (err) {
				console.error('Failed to convert HEIC/HEIF profile photo:', err);
				setMessage({ type: 'error', text: 'Could not process HEIC/HEIF photo. Please try another image.' });
				return;
			}
		}

		// Store the file to upload later
		setSelectedProfilePictureFile(fileForUpload);

		// Create preview using FileReader
		const reader = new FileReader();
		reader.onloadend = () => {
			setProfilePicturePreview(reader.result as string);
		};
		reader.readAsDataURL(fileForUpload);
	};

	const handleSaveProfile = async () => {
		if (!user?.id) return;
		
		setSaving(true);
		setMessage(null);
		
		try {
			// First, upload profile picture if a new one was selected
			let uploadedProfilePictureUrl: string | null = null;
			if (selectedProfilePictureFile) {
				const formData = new FormData();
				formData.append('image', selectedProfilePictureFile);
				formData.append('type', 'profile');
				formData.append('subtype', 'picture');

				const response = await fetch(`${API_BASE_URL}/upload`, {
					method: 'POST',
					body: formData,
				});

				const result = await response.json();
				
				if (result.success && result.data?.url) {
					// Store the uploaded URL to save to database
					uploadedProfilePictureUrl = result.data.url;
					// Update profile picture URL with the new one
					setProfilePicture(result.data.url);
					// Clear preview and selected file since it's now uploaded
					setProfilePicturePreview(null);
					setSelectedProfilePictureFile(null);
				} else {
					// Show detailed error message if available
					const errorMsg = result.error || 'Failed to upload profile picture';
					const debugInfo = result.details ? ` (${JSON.stringify(result.details)})` : '';
					console.error('Upload error:', result);
					setMessage({ type: 'error', text: errorMsg + debugInfo });
					setSaving(false);
					return; // Don't continue with profile update if upload failed
				}
			}

			// Then save profile data (including profile picture URL if uploaded)
			const profileData: any = {
				userId: user.id,
				bio: bio,
				location: location,
				phone: phone,
				country_code: countryCode || null,
				skills: skills,
			};
			
			// Include profile picture URL if we just uploaded one
			if (uploadedProfilePictureUrl) {
				profileData.profile_picture_url = uploadedProfilePictureUrl;
			}
			
			await apiPut('/users/me/profile', profileData);
			
			setMessage({ type: 'success', text: 'Profile updated successfully' });
		} catch (error) {
			console.error('Error saving profile:', error);
			setMessage({ type: 'error', text: 'Failed to save profile' });
		} finally {
			setSaving(false);
		}
	};

	const handleSaveSettings = async () => {
		if (!user?.id) return;
		
		setSaving(true);
		setMessage(null);
		
		try {
			await apiPut('/users/me/settings', {
				userId: user.id,
				email_notifications: emailNotifications,
				sms_notifications: smsNotifications,
				dark_mode: darkMode,
			});
			
			setMessage({ type: 'success', text: 'Settings updated successfully' });
		} catch (error) {
			console.error('Error saving settings:', error);
			setMessage({ type: 'error', text: 'Failed to save settings' });
		} finally {
			setSaving(false);
		}
	};

	const handleSaveSchedule = async () => {
		if (!user?.id || !companyData?.company) return;
		setScheduleSaving(true);
		setMessage(null);
		try {
			await updateCompanySchedule(user.id, {
				working_days: workingDays.length > 0 ? workingDays : null,
				non_working_dates: nonWorkingDates.map((d) => ({ date: d.date, label: d.label ?? undefined })),
			});
			const data = await getMyCompany(user.id, user.activeCompanyId ?? undefined);
			setCompanyData(data);
			if (data.company) {
				const wd = data.company.working_days;
				setWorkingDays(Array.isArray(wd) && wd.length > 0 ? wd : [1, 2, 3, 4, 5]);
				setNonWorkingDates(Array.isArray(data.company.non_working_dates) ? data.company.non_working_dates : []);
			}
			setMessage({ type: 'success', text: 'Working days and holidays updated.' });
		} catch (e: any) {
			setMessage({ type: 'error', text: e.message || 'Failed to save schedule' });
		} finally {
			setScheduleSaving(false);
		}
	};

	const handleSaveCompanyCountry = async () => {
		if (!user?.id || !companyData?.company) return;
		setCompanyActionLoading(true);
		setMessage(null);
		try {
			await updateMyCompanyProfile(user.id, { countryCode: companyCountryCode || null });
			const data = await getMyCompany(user.id, user.activeCompanyId ?? undefined);
			setCompanyData(data);
			if (data.company) {
				setCompanyCountryCode((data.company.country_code || '').toUpperCase());
			}
			setMessage({ type: 'success', text: copy('Company country updated.', 'Unternehmensland aktualisiert.', 'Država tvrtke ažurirana.') });
		} catch (e: any) {
			setMessage({ type: 'error', text: e.message || copy('Failed to update company country', 'Unternehmensland konnte nicht aktualisiert werden', 'Ažuriranje države tvrtke nije uspjelo') });
		} finally {
			setCompanyActionLoading(false);
		}
	};

	const WEEKDAY_LABELS = [
		copy('Sun', 'So', 'Ned'),
		copy('Mon', 'Mo', 'Pon'),
		copy('Tue', 'Di', 'Uto'),
		copy('Wed', 'Mi', 'Sri'),
		copy('Thu', 'Do', 'Čet'),
		copy('Fri', 'Fr', 'Pet'),
		copy('Sat', 'Sa', 'Sub')
	];
	const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Display Monday–Sunday, but keep 0=Sun..6=Sat for storage
	const availableInviteGardeners = inviteGardeners.filter((g) => !inviteMemberIds.has(g.id));
	const filteredInviteGardeners = availableInviteGardeners.filter((g) => {
		const q = inviteSearch.trim().toLowerCase();
		if (!q) return true;
		return `${g.name} ${g.email}`.toLowerCase().includes(q);
	});
	const getInviteGardenerLabel = (g: UserResponse) => `${g.name} (${g.email})`;

	if (loading) {
		return (
			<>
								<h1 className="h3 mb-4">{copy('Settings', 'Einstellungen', 'Postavke')}</h1>
				<div className="text-center py-5">
					<div className="spinner-border text-success" role="status">
						<span className="visually-hidden">{copy('Loading...', 'Lädt...', 'Učitavanje...')}</span>
					</div>
				</div>
			</>
		);
	}

	return (
		<>
			<h1 className="h3 mb-4">{copy('Settings', 'Einstellungen', 'Postavke')}</h1>
			
			{message && (
				<div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`} role="alert">
					{message.text}
					<button type="button" className="btn-close" onClick={() => setMessage(null)} aria-label="Close"></button>
				</div>
			)}
			
			{/* Row 1: Profile (left) and Skills (right for gardener/company) — minimizes scrolling */}
			<div className="row g-4">
				<div className={user.role === 'gardener' || user.role === 'company' ? 'col-12 col-lg-6' : 'col-12'}>
					<div className="card h-100" data-section-id="account">
						<div className="card-body d-flex flex-column">
							<div>
								<h5 className="card-title mb-3">{copy('Profile', 'Profil', 'Profil')}</h5>
								
								{/* Profile Picture */}
								<div className="mb-3">
									<label className="form-label">{copy('Profile Picture', 'Profilbild', 'Profilna slika')}</label>
									<div className="d-flex align-items-center gap-3 mb-2">
										{profilePicturePreview ? (
											<img 
												src={profilePicturePreview} 
												alt="Profile Preview" 
												className="rounded-circle"
												style={{ width: '80px', height: '80px', objectFit: 'cover' }}
											/>
										) : profilePicture ? (
											<img 
												src={getImageUrl(profilePicture) || ''} 
												alt="Profile" 
												className="rounded-circle"
												style={{ width: '80px', height: '80px', objectFit: 'cover' }}
												onError={() => setProfilePicture(null)}
											/>
										) : (
											<div 
												className="rounded-circle bg-secondary d-flex align-items-center justify-content-center"
												style={{ width: '80px', height: '80px' }}
											>
												<i className="bi bi-person-fill text-white" style={{ fontSize: '2rem' }}></i>
											</div>
										)}
										<div>
											<input
												ref={fileInputRef}
												type="file"
												accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
												onChange={handleProfilePictureChange}
												className="form-control form-control-sm"
												disabled={saving}
												style={{ display: 'none' }}
											/>
											<button
												type="button"
												className="btn btn-sm btn-outline-primary"
												onClick={() => fileInputRef.current?.click()}
												disabled={saving}
											>
												{saving
													? copy('Uploading...', 'Lädt hoch...', 'Učitavanje...')
													: copy('Change Picture', 'Bild ändern', 'Promijeni sliku')}
											</button>
										</div>
									</div>
									<small className="text-muted">
										{copy(
											'Max size: 5MB. Supported formats: JPEG, PNG, WebP, HEIC, HEIF',
											'Maximale Größe: 5MB. Unterstützte Formate: JPEG, PNG, WebP, HEIC, HEIF',
											'Maksimalna veličina: 5MB. Podržani formati: JPEG, PNG, WebP, HEIC, HEIF'
										)}
									</small>
								</div>
								
								<form className="vstack gap-3">
									<div>
										<label htmlFor="settings-name" className="form-label">{copy('Name', 'Name', 'Ime')}</label>
										<input id="settings-name" className="form-control" defaultValue={user.name} readOnly />
									</div>
									<div>
										<label htmlFor="settings-email" className="form-label">{copy('Email address', 'E-Mail-Adresse', 'Adresa e-pošte')}</label>
										<input id="settings-email" type="email" className="form-control" defaultValue={user.email} readOnly />
									</div>
									<div>
										<label htmlFor="settings-role" className="form-label">{copy('Role', 'Rolle', 'Uloga')}</label>
										<select id="settings-role" className="form-select" value={user.role} disabled>
											<option value="admin">{copy('Administrator', 'Administrator', 'Administrator')}</option>
											<option value="company">{copy('Company', 'Unternehmen', 'Tvrtka')}</option>
											<option value="gardener">{copy('Gardener', 'Gärtner', 'Vrtlar')}</option>
											<option value="seeker">{copy('Garden Seeker', 'Gartensuchende', 'Vlasnik vrta')}</option>
										</select>
									</div>
									{(user.companyName || user.role === 'company' || user.role === 'gardener') && (
										<div>
											<label htmlFor="settings-company" className="form-label">{copy('Active company', 'Aktives Unternehmen', 'Aktivna tvrtka')}</label>
											<input
												id="settings-company"
												className="form-control"
												value={user.companyName || companyData?.company?.name || '—'}
												readOnly
											/>
											{membershipContexts && membershipContexts.companies.length > 1 && (
												<div className="form-text">
													{copy('All your teams:', 'Alle Ihre Teams:', 'Svi vaši timovi:')}{' '}
													{membershipContexts.companies.map((c) => c.name).join(' · ')}
												</div>
											)}
										</div>
									)}
									<div>
										<label htmlFor="settings-bio" className="form-label">{copy('Bio', 'Bio', 'Opis')}</label>
										<textarea 
											id="settings-bio" 
											className="form-control" 
											rows={3}
											value={bio}
											onChange={(e) => setBio(e.target.value)}
											placeholder={copy('Tell us about yourself...', 'Erzählen Sie etwas über sich...', 'Recite nešto o sebi...')}
										/>
									</div>
									<div>
										<label htmlFor="settings-location" className="form-label">{copy('Location', 'Standort', 'Lokacija')}</label>
										<input 
											id="settings-location" 
											className="form-control" 
											value={location}
											onChange={(e) => setLocation(e.target.value)}
											placeholder={copy('City, Country', 'Stadt, Land', 'Grad, država')}
										/>
									</div>
									<div>
										<label htmlFor="settings-phone" className="form-label">{copy('Phone', 'Telefon', 'Telefon')}</label>
										<input 
											id="settings-phone" 
											type="tel"
											className="form-control" 
											value={phone}
											onChange={(e) => setPhone(e.target.value)}
											placeholder="+385 91 234 5678"
										/>
									</div>
									<div>
										<label htmlFor="settings-country" className="form-label">{copy('Your country', 'Ihr Land', 'Vaša država')}</label>
										<select
											id="settings-country"
											className="form-select"
											value={countryCode}
											onChange={(e) => setCountryCode(e.target.value)}
										>
											<option value="">{copy('Select country (optional)', 'Land auswählen (optional)', 'Odaberite državu (opcionalno)')}</option>
											{COUNTRY_OPTIONS.map((opt) => (
												<option key={opt.code} value={opt.code}>{opt.label}</option>
											))}
										</select>
										<div className="form-text">
											{copy('Used for legal timeline warnings when no company country is set.', 'Wird für rechtliche Zeitplanwarnungen verwendet, wenn kein Unternehmensland gesetzt ist.', 'Koristi se za pravna upozorenja u rasporedu kada država tvrtke nije postavljena.')}
										</div>
									</div>
									<div className="text-muted small">
										{copy(
											'Account information (name, email, role) is managed by your HortEra administrator. Contact support to request changes.',
											'Kontoinformationen (Name, E-Mail, Rolle) werden von Ihrem HortEra-Administrator verwaltet. Wenden Sie sich für Änderungen an den Support.',
											'Podacima računa (ime, e-pošta, uloga) upravlja administrator HortEre. Za promjene kontaktirajte podršku.'
										)}
									</div>
								</form>
							</div>
							<div className="mt-auto pt-3 d-flex flex-column gap-2">
								<button 
									type="button" 
									className="btn btn-success"
									onClick={handleSaveProfile}
									disabled={saving}
								>
									{saving ? copy('Saving...', 'Speichern...', 'Spremanje...') : copy('Save Profile', 'Profil speichern', 'Spremi profil')}
								</button>
								<button type="button" className="btn btn-outline-danger" onClick={handleLogout}>
									<i className="bi bi-box-arrow-right me-2"></i>
									{copy('Logout', 'Abmelden', 'Odjava')}
								</button>
								<button
									type="button"
									className="btn btn-outline-danger"
									onClick={handleDeleteAccount}
									disabled={saving}
								>
									<i className="bi bi-person-x me-2"></i>
									{copy('Delete my profile', 'Mein Profil löschen', 'Izbriši moj profil')}
								</button>
							</div>
						</div>
					</div>
				</div>

				{(user.role === 'gardener' || user.role === 'company') && (
					<div className="col-12 col-lg-6">
						<div className="card" data-section-id="company">
							<div className="card-body">
								<h5 className="card-title mb-3">{copy('Company', 'Unternehmen', 'Tvrtka')}</h5>
								{membershipContexts && membershipContexts.companies.length > 0 && (
									<div className="mb-3 p-2 rounded border bg-light-subtle">
										<div className="small fw-semibold text-muted mb-2">{copy('Your organizations', 'Ihre Organisationen', 'Vaše organizacije')}</div>
										<ul className="list-unstyled mb-2 small">
											{membershipContexts.companies.map((c) => {
												const cid = Number(c.id);
												const activeRaw =
													membershipContexts.active_company_id ??
													companyData?.activeCompanyId ??
													user.activeCompanyId;
												const isActive =
													activeRaw != null && !Number.isNaN(cid) && cid === Number(activeRaw);
												return (
													<li
														key={c.id}
														className="d-flex align-items-center justify-content-between gap-2 py-1 border-bottom border-light"
													>
														<span>
															{c.name}
															{isActive ? (
																<span className="badge text-bg-success ms-2">{copy('Active', 'Aktiv', 'Aktivno')}</span>
															) : null}
														</span>
														{!isActive ? (
															<button
																type="button"
																className="btn btn-sm btn-outline-success"
																disabled={switchingCompany}
																onClick={async () => {
																	if (!user?.id) return;
																	setSwitchingCompany(true);
																	setMessage(null);
																	try {
																		await setMyActiveCompany(user.id, c.id);
																		await refreshUser(user.id);
																		const [nextCompany, nextCtx] = await Promise.all([
																			getMyCompany(user.id, c.id),
																			getMyCompanyContexts(user.id),
																		]);
																		setCompanyData(nextCompany);
																		setMembershipContexts(nextCtx);
																		if (nextCompany?.company) {
																			const wd = nextCompany.company.working_days;
																			setWorkingDays(
																				Array.isArray(wd) && wd.length > 0 ? wd : [1, 2, 3, 4, 5]
																			);
																			setNonWorkingDates(
																				Array.isArray(nextCompany.company.non_working_dates)
																					? nextCompany.company.non_working_dates
																					: []
																			);
																		}
																		if (user.role === 'company' && nextCompany?.company) {
																			try {
																				const [gardeners, membersRes] = await Promise.all([
																					getUsers({ role: 'gardener' }),
																					getMyCompanyMembers(user.id, nextCompany.company.id),
																				]);
																				setInviteGardeners(gardeners);
																				setInviteMemberIds(
																					new Set((membersRes.members || []).map((m) => m.id))
																				);
																			} catch (_e) {}
																		}
																		setMessage({
																			type: 'success',
																			text: `Active company is now “${c.name}”.`,
																		});
																		notifyCompanyWorkspaceChanged();
																	} catch (e: any) {
																		setMessage({
																			type: 'error',
																			text: e.message || 'Could not switch company',
																		});
																	} finally {
																		setSwitchingCompany(false);
																	}
																}}
															>
																{copy('Switch here', 'Hier wechseln', 'Prebaci ovdje')}
															</button>
														) : null}
													</li>
												);
											})}
										</ul>
										<p className="small text-muted mb-0">
											{membershipContexts.companies.length > 1
												? copy(
													'Use the profile menu (top right) to switch quickly without opening Settings.',
													'Nutzen Sie das Profilmenü (oben rechts), um schnell zu wechseln, ohne Einstellungen zu öffnen.',
													'Koristite profilni izbornik (gore desno) za brzo prebacivanje bez otvaranja postavki.'
												)
												: copy(
													'You are on one team. Request to join others below, or accept an invite.',
													'Sie sind in einem Team. Fordern Sie unten den Beitritt zu weiteren an oder akzeptieren Sie eine Einladung.',
													'Trenutno ste u jednom timu. Zatražite pridruživanje drugima ispod ili prihvatite poziv.'
												)}
										</p>
									</div>
								)}
								{companyData?.company && (
									<p className="text-muted small mb-3">
										{copy('Managing:', 'Verwaltet:', 'Upravljanje:')} <strong>{companyData.company.name}</strong>
									</p>
								)}
								{user.role === 'company' && companyData?.company && (
									<div className="mb-3">
										<label className="form-label small fw-semibold">{copy('Company country', 'Unternehmensland', 'Država tvrtke')}</label>
										<div className="d-flex gap-2 flex-wrap align-items-center">
											<select
												className="form-select form-select-sm"
												style={{ maxWidth: '260px' }}
												value={companyCountryCode}
												onChange={(e) => setCompanyCountryCode(e.target.value)}
												disabled={companyActionLoading}
											>
												<option value="">{copy('Select country', 'Land auswählen', 'Odaberite državu')}</option>
												{COUNTRY_OPTIONS.map((opt) => (
													<option key={opt.code} value={opt.code}>{opt.label}</option>
												))}
											</select>
											<button
												type="button"
												className="btn btn-sm btn-success"
												disabled={companyActionLoading}
												onClick={handleSaveCompanyCountry}
											>
												{companyActionLoading ? copy('Saving…', 'Speichern…', 'Spremanje…') : copy('Save country', 'Land speichern', 'Spremi državu')}
											</button>
										</div>
										<p className="text-muted small mt-1 mb-0">
											{copy('Germany enables legal seasonal cutting warnings in generated plans.', 'Deutschland aktiviert rechtliche saisonale Schnittwarnungen in generierten Plänen.', 'Njemačka omogućuje pravna sezonska upozorenja za rezanje u generiranim planovima.')}
										</p>
									</div>
								)}
								{user.role === 'company' && !companyData?.company && (
									<>
										<p className="text-muted small mb-2">Create your company to manage your team.</p>
										<div className="d-flex gap-2 mb-3">
											<input
												type="text"
												className="form-control"
												placeholder="Company name"
												value={newCompanyName}
												onChange={(e) => setNewCompanyName(e.target.value)}
												disabled={companyActionLoading}
											/>
											<button
												type="button"
												className="btn btn-success"
												disabled={!newCompanyName.trim() || companyActionLoading}
												onClick={async () => {
													if (!user?.id) return;
													setCompanyActionLoading(true);
													setMessage(null);
													try {
														await createCompany(user.id, newCompanyName.trim());
														setNewCompanyName('');
														await refreshUser(user.id);
														const [data, ctxRes] = await Promise.all([
															getMyCompany(user.id),
															getMyCompanyContexts(user.id),
														]);
														setCompanyData(data);
														setMembershipContexts(ctxRes);
														setMessage({ type: 'success', text: 'Company created.' });
													} catch (e: any) {
														setMessage({ type: 'error', text: e.message || 'Failed to create company' });
													} finally {
														setCompanyActionLoading(false);
													}
												}}
											>
												{companyActionLoading ? 'Creating…' : 'Create'}
											</button>
										</div>
									</>
								)}
								{user.role === 'company' && companyData?.pendingRequests && companyData.pendingRequests.length > 0 && (
									<div className="mb-3">
										<label className="form-label small fw-semibold">Pending join requests</label>
										<ul className="list-group list-group-flush">
											{companyData.pendingRequests.map((req) => (
												<li key={req.id} className="list-group-item d-flex justify-content-between align-items-center px-0">
													<div>
														<span className="fw-medium">{req.name}</span>
														<small className="text-muted d-block">{req.email}</small>
													</div>
													<div className="d-flex gap-1">
														<button
															type="button"
															className="btn btn-sm btn-success"
															disabled={companyActionLoading}
															onClick={async () => {
																if (!user?.id) return;
																setCompanyActionLoading(true);
																setMessage(null);
																try {
																	await approveCompanyMember(user.id, req.id);
																	const data = await getMyCompany(
																		user.id,
																		user.activeCompanyId ?? undefined
																	);
																	setCompanyData(data);
																	setMessage({ type: 'success', text: 'Request approved.' });
																} catch (e: any) {
																	setMessage({ type: 'error', text: e.message || 'Failed' });
																} finally {
																	setCompanyActionLoading(false);
																}
															}}
														>
															Approve
														</button>
														<button
															type="button"
															className="btn btn-sm btn-outline-secondary"
															disabled={companyActionLoading}
															onClick={async () => {
																if (!user?.id) return;
																setCompanyActionLoading(true);
																setMessage(null);
																try {
																	await rejectCompanyMember(user.id, req.id);
																	const data = await getMyCompany(user.id);
																	setCompanyData(data);
																	setMessage({ type: 'success', text: 'Request rejected.' });
																} catch (e: any) {
																	setMessage({ type: 'error', text: e.message || 'Failed' });
																} finally {
																	setCompanyActionLoading(false);
																}
															}}
														>
															Reject
														</button>
													</div>
												</li>
											))}
										</ul>
									</div>
								)}
								{user.role === 'company' && companyData?.company && (
									<div className="mb-3">
										<label className="form-label small fw-semibold">Invite gardener</label>
										<p className="text-muted small mb-2">Invite gardeners either by selecting an existing account or by creating a link/code for someone who has not registered yet.</p>
										<div className="btn-group btn-group-sm mb-2" role="tablist">
											<button
												type="button"
												className={`btn ${inviteMode === 'existing' ? 'btn-success' : 'btn-outline-success'}`}
												onClick={() => {
													setInviteMode('existing');
													setInviteSearch('');
													setInviteSelectedId('');
													setInviteError(null);
													setCreatedInviteCode(null);
													setCreatedInviteLink(null);
													setInviteActionMessage(null);
												}}
												disabled={companyActionLoading || inviteLoading}
											>
												Existing account
											</button>
											<button
												type="button"
												className={`btn ${inviteMode === 'new' ? 'btn-success' : 'btn-outline-success'}`}
												onClick={() => {
													setInviteMode('new');
													setInviteError(null);
													setInviteActionMessage(null);
												}}
												disabled={companyActionLoading || inviteLoading}
											>
												Invite by link/code
											</button>
										</div>
										{inviteError && (
											<div className="alert alert-danger py-2 mb-2 small">{inviteError}</div>
										)}
										{inviteMode === 'existing' ? (
											<>
												<div className="d-flex gap-2 flex-wrap align-items-start">
													<input
														list="settings-invite-gardener-options"
														className="form-control form-control-sm"
														style={{ maxWidth: '320px' }}
														placeholder="Type to search and pick gardener…"
														value={inviteSearch}
														onChange={(e) => {
															const nextValue = e.target.value;
															setInviteSearch(nextValue);
															const normalized = nextValue.trim().toLowerCase();
															const exact = availableInviteGardeners.find((g) => {
																const label = getInviteGardenerLabel(g).toLowerCase();
																return label === normalized || g.email.toLowerCase() === normalized;
															});
															setInviteSelectedId(exact ? exact.id : '');
															setInviteError(null);
														}}
														disabled={companyActionLoading || inviteLoading}
													/>
													<datalist id="settings-invite-gardener-options">
														{filteredInviteGardeners.map((g) => (
															<option key={g.id} value={getInviteGardenerLabel(g)} />
														))}
													</datalist>
													<button
														type="button"
														className="btn btn-sm btn-success"
														disabled={inviteSelectedId === '' || companyActionLoading || inviteLoading}
														onClick={async () => {
															if (!user?.id || companyData?.company == null || inviteSelectedId === '') return;
															setInviteLoading(true);
															setCompanyActionLoading(true);
															setInviteError(null);
															setMessage(null);
															try {
																await inviteToCompany(user.id, companyData.company.id, inviteSelectedId);
																setInviteSelectedId('');
																setInviteSearch('');
																setInviteMemberIds((prev) => new Set([...prev, inviteSelectedId]));
																setMessage({
																	type: 'success',
																	text: copy(
																		'Invite sent. The gardener can accept or decline in Settings.',
																		'Einladung gesendet. Der Gärtner kann sie in den Einstellungen annehmen oder ablehnen.',
																		'Pozivnica je poslana. Vrtlar je može prihvatiti ili odbiti u Postavkama.'
																	)
																});
															} catch (e: any) {
																setInviteError(e.message || copy('Failed to send invite', 'Senden der Einladung fehlgeschlagen', 'Slanje pozivnice nije uspjelo'));
															} finally {
																setInviteLoading(false);
																setCompanyActionLoading(false);
															}
														}}
													>
														{inviteLoading ? copy('Sending…', 'Senden…', 'Slanje…') : copy('Send invite', 'Einladung senden', 'Pošalji poziv')}
													</button>
												</div>
												{inviteSearch.trim() && filteredInviteGardeners.length === 0 && (
													<p className="text-muted small mb-0 mt-1">
														{copy('No gardeners match your search.', 'Keine Gärtner passen zu Ihrer Suche.', 'Nijedan vrtlar ne odgovara vašoj pretrazi.')}
													</p>
												)}
											</>
										) : (
											<>
												<p className="text-muted small mb-2">
													{copy(
														'Create a link/code for a gardener who does not have an account yet. They can register from the link and then accept your company invite.',
														'Erstellen Sie einen Link oder Code für einen Gärtner, der noch kein Konto hat. Er kann sich über den Link registrieren und danach Ihre Unternehmenseinladung annehmen.',
														'Izradite poveznicu ili kod za vrtlara koji još nema račun. Može se registrirati putem poveznice, a zatim prihvatiti poziv u vašu tvrtku.'
													)}
												</p>
												<button
													type="button"
													className="btn btn-sm btn-success"
													disabled={companyActionLoading || inviteLoading}
													onClick={async () => {
														if (!user?.id) return;
														setInviteLoading(true);
														setCompanyActionLoading(true);
														setInviteError(null);
														setMessage(null);
														try {
															const created = await createGardenerInvite({ userId: Number(user.id) });
															setCreatedInviteCode(created.code);
															const link = buildAppUrl(`?gardenerInvite=${encodeURIComponent(created.code)}`);
															setCreatedInviteLink(link);
															setMessage({ type: 'success', text: copy('Invite link created.', 'Einladungslink erstellt.', 'Poveznica pozivnice je izrađena.') });
														} catch (e: any) {
															setInviteError(e.message || copy('Failed to create invite', 'Einladung konnte nicht erstellt werden', 'Kreiranje pozivnice nije uspjelo'));
														} finally {
															setInviteLoading(false);
															setCompanyActionLoading(false);
														}
													}}
												>
													{inviteLoading ? copy('Creating…', 'Erstellen…', 'Stvaranje…') : copy('Create invite link', 'Einladungslink erstellen', 'Izradi poveznicu pozivnice')}
												</button>
												{createdInviteCode && (
													<div className="alert alert-success small mt-2 mb-0">
														<div className="mb-2">
															<strong>{copy('Invite created!', 'Einladung erstellt!', 'Pozivnica je izrađena!')}</strong> {copy('Code:', 'Code:', 'Kod:')}{' '}
															<code className="fw-bold">{createdInviteCode}</code>
														</div>
														{createdInviteLink && (
															<div className="d-flex flex-wrap gap-2 align-items-center">
																<code style={{ wordBreak: 'break-all' }}>{createdInviteLink}</code>
																<button
																	type="button"
																	className="btn btn-sm btn-outline-success"
																	onClick={async () => {
																		if (!createdInviteLink) return;
																		await navigator.clipboard?.writeText(createdInviteLink);
																		setInviteActionMessage(copy('Link copied', 'Link kopiert', 'Poveznica je kopirana'));
																		window.setTimeout(() => setInviteActionMessage(null), 2200);
																	}}
																>
																	{copy('Copy link', 'Link kopieren', 'Kopiraj poveznicu')}
																</button>
																<button
																	type="button"
																	className="btn btn-sm btn-success"
																	onClick={() => {
																		if (!createdInviteLink || !createdInviteCode) return;
																		const text = copy(
																			`Join our company on HortEra.\n\nUse this invite link:\n${createdInviteLink}\n\nInvite code: ${createdInviteCode}`,
																			`Treten Sie unserem Unternehmen auf HortEra bei.\n\nVerwenden Sie diesen Einladungslink:\n${createdInviteLink}\n\nEinladungscode: ${createdInviteCode}`,
																			`Pridružite se našoj tvrtki na HortEri.\n\nUpotrijebite ovu poveznicu pozivnice:\n${createdInviteLink}\n\nKod pozivnice: ${createdInviteCode}`
																		);
																		openWhatsAppShare(text);
																		setInviteActionMessage(copy('Opening WhatsApp...', 'WhatsApp wird geöffnet...', 'Otvara se WhatsApp...'));
																		window.setTimeout(() => setInviteActionMessage(null), 2200);
																	}}
																>
																	<i className="bi bi-whatsapp me-1" />
																	{copy('Share on WhatsApp', 'Podijeli putem WhatsAppa', 'Podijeli na WhatsAppu')}
																</button>
																{inviteActionMessage && (
																	<div className="text-success small w-100">{inviteActionMessage}</div>
																)}
															</div>
														)}
													</div>
												)}
											</>
										)}
										{availableInviteGardeners.length === 0 && inviteGardeners.length > 0 && inviteMode === 'existing' && (
											<p className="text-muted small mb-0 mt-1">All gardeners are already in your company or have a pending request/invite.</p>
										)}
									</div>
								)}
								{companyData?.company && (
									<div className="mb-3" data-section-id="working-days">
										<hr className="my-3" />
										<label className="form-label small fw-semibold">{copy('Working days & holidays', 'Arbeitstage & Feiertage', 'Radni dani i praznici')}</label>
										<p className="text-muted small mb-2">{copy('Set which weekdays your company works. Offers and scheduling will use only these days. Add specific closed dates (e.g. holidays) below.', 'Legen Sie fest, an welchen Wochentagen Ihr Unternehmen arbeitet. Angebote und Planung nutzen nur diese Tage. Fügen Sie unten geschlossene Termine hinzu (z. B. Feiertage).', 'Postavite koje dane u tjednu vaša tvrtka radi. Ponude i rasporedi koristit će samo te dane. Dodajte specifične neradne datume (npr. praznike) ispod.')}</p>
										{user.role === 'company' && (
											<>
												<div className="mb-2">
													<span className="d-block small mb-1">{copy('Working weekdays', 'Radni dani u tjednu', 'Radni dani u tjednu')}</span>
													<div className="d-flex flex-wrap gap-2">
														{WEEKDAY_ORDER.map((dayNum) => {
															const label = WEEKDAY_LABELS[dayNum];
															const checked = workingDays.includes(dayNum);
															return (
																<button
																	type="button"
																	key={dayNum}
																	className={`btn btn-sm ${checked ? 'btn-success' : 'btn-outline-success'} mb-0`}
																	style={{ minWidth: '2.5rem' }}
																	onClick={() => {
																		if (checked) {
																			setWorkingDays(workingDays.filter((d) => d !== dayNum));
																		} else {
																			setWorkingDays([...workingDays, dayNum].sort((a, b) => a - b));
																		}
																	}}
																>
																	{label}
																</button>
															);
														})}
													</div>
												</div>
												<div className="mb-2">
													<span className="d-block small mb-1">{copy('Closed dates (holidays, etc.)', 'Geschlossene Termine (Feiertage usw.)', 'Neradni datumi (praznici itd.)')}</span>
													<ul className="list-group list-group-flush small mb-2">
														{nonWorkingDates.map((d, idx) => (
															<li key={d.id ? `id-${d.id}` : `new-${idx}-${d.date}`} className="list-group-item d-flex justify-content-between align-items-center px-0 py-1">
																<span>{d.date}{d.label ? ` — ${d.label}` : ''}</span>
																<button
																	type="button"
																	className="btn btn-sm btn-outline-danger py-0"
																	onClick={() => setNonWorkingDates(nonWorkingDates.filter((_, i) => i !== idx))}
																	aria-label="Remove"
																>
																	<i className="bi bi-x" />
																</button>
															</li>
														))}
														{nonWorkingDates.length === 0 && (
															<li className="list-group-item text-muted px-0 py-1">{copy('No closed dates added.', 'Keine geschlossenen Termine hinzugefügt.', 'Nema dodanih neradnih datuma.')}</li>
														)}
													</ul>
													<div className="d-flex gap-2 flex-wrap align-items-end">
														<div>
															<input
																type="date"
																className="form-control form-control-sm"
																value={newClosedDate}
																onChange={(e) => setNewClosedDate(e.target.value)}
															/>
														</div>
														<div style={{ minWidth: '140px' }}>
															<input
																type="text"
																className="form-control form-control-sm"
																placeholder={copy('Label (optional)', 'Oznaka (optional)', 'Oznaka (opcionalno)')}
																value={newClosedLabel}
																onChange={(e) => setNewClosedLabel(e.target.value)}
															/>
														</div>
														<button
															type="button"
															className="btn btn-sm btn-success"
															disabled={!newClosedDate || scheduleSaving}
															onClick={() => {
																if (!newClosedDate) return;
																setNonWorkingDates([...nonWorkingDates, { id: 0, date: newClosedDate, label: newClosedLabel.trim() || null }].sort((a, b) => a.date.localeCompare(b.date)));
																setNewClosedDate('');
																setNewClosedLabel('');
															}}
														>
															{copy('Add', 'Hinzufügen', 'Dodaj')}
														</button>
													</div>
												</div>
												<button
													type="button"
													className="btn btn-success btn-sm"
													disabled={scheduleSaving}
													onClick={handleSaveSchedule}
												>
													{scheduleSaving ? copy('Saving…', 'Speichern…', 'Spremanje…') : copy('Save working days & holidays', 'Arbeitstage & Feiertage speichern', 'Spremi radne dane i praznike')}
												</button>
											</>
										)}
										{user.role === 'gardener' && companyData.company && (
											<div className="small">
												<div className="text-muted mb-1">{copy('Working weekdays:', 'Arbeitstage:', 'Radni dani:')} {workingDays.length === 0 ? copy('Not set', 'Nicht festgelegt', 'Nije postavljeno') : workingDays.map((d) => WEEKDAY_LABELS[d]).join(', ')}</div>
												{nonWorkingDates.length > 0 && (
													<div className="text-muted">{copy('Closed dates:', 'Geschlossene Termine:', 'Neradni datumi:')} {nonWorkingDates.map((d) => d.date + (d.label ? ` (${d.label})` : '')).join(', ')}</div>
												)}
												<p className="text-muted mt-1 mb-0">{copy('Only company owners can edit. Ask your company admin to change these in their Settings.', 'Nur Unternehmensinhaber können dies bearbeiten. Bitten Sie Ihren Unternehmens-Admin, dies in den Einstellungen zu ändern.', 'Samo vlasnici tvrtke mogu uređivati. Zamolite administratora tvrtke da to promijeni u postavkama.')}</p>
											</div>
										)}
									</div>
								)}
								{(user.role === 'gardener' || user.role === 'company') && (
									<div className="mb-3" data-section-id="my-time-off">
										<hr className="my-3" />
										<label className="form-label small fw-semibold">{copy('My time off (PTO)', 'Meine Abwesenheit (PTO)', 'Moj godišnji odmor (PTO)')}</label>
										<p className="text-muted small mb-2">{copy('Add dates when you are not available. These are used when scheduling work and assigning you to tasks.', 'Fügen Sie Termine hinzu, an denen Sie nicht verfügbar sind. Diese werden für Planung und Aufgabenzuweisung verwendet.', 'Dodajte datume kada niste dostupni. Koriste se pri planiranju rada i dodjeli zadataka.')}</p>
										<ul className="list-group list-group-flush small mb-2">
											{timeOffEntries.map((entry) => (
												<li key={entry.id} className="list-group-item d-flex justify-content-between align-items-center px-0 py-1">
													<span>
														{entry.start_date === entry.end_date
															? entry.start_date
															: `${entry.start_date} – ${entry.end_date}`}
														{entry.label ? ` — ${entry.label}` : ''}
													</span>
													<button
														type="button"
														className="btn btn-sm btn-outline-danger py-0"
														disabled={ptoLoading}
														onClick={async () => {
															if (!user?.id) return;
															setPtoLoading(true);
															setMessage(null);
															try {
																await deleteTimeOff(user.id, entry.id);
																setTimeOffEntries((prev) => prev.filter((e) => e.id !== entry.id));
																setMessage({ type: 'success', text: 'Time off removed.' });
															} catch (e: any) {
																setMessage({ type: 'error', text: e.message || 'Failed to remove' });
															} finally {
																setPtoLoading(false);
															}
														}}
														aria-label="Remove"
													>
														<i className="bi bi-x" />
													</button>
												</li>
											))}
											{timeOffEntries.length === 0 && (
												<li className="list-group-item text-muted px-0 py-1">{copy('No time off added.', 'Keine Abwesenheit hinzugefügt.', 'Nema dodanih dana odsutnosti.')}</li>
											)}
										</ul>
										<div className="d-flex gap-2 flex-wrap align-items-end">
											<div>
												<span className="d-block small text-muted mb-1">{copy('From', 'Von', 'Od')}</span>
												<input
													type="date"
													className="form-control form-control-sm"
													value={ptoStartDate}
													onChange={(e) => setPtoStartDate(e.target.value)}
												/>
											</div>
											<div>
												<span className="d-block small text-muted mb-1">{copy('To', 'Bis', 'Do')}</span>
												<input
													type="date"
													className="form-control form-control-sm"
													value={ptoEndDate}
													onChange={(e) => setPtoEndDate(e.target.value)}
												/>
											</div>
											<div style={{ minWidth: '140px' }}>
												<span className="d-block small text-muted mb-1">{copy('Label (optional)', 'Bezeichnung (optional)', 'Oznaka (opcionalno)')}</span>
												<input
													type="text"
													className="form-control form-control-sm"
													placeholder={copy('e.g. Vacation', 'z. B. Urlaub', 'npr. odmor')}
													value={ptoLabel}
													onChange={(e) => setPtoLabel(e.target.value)}
												/>
											</div>
											<button
												type="button"
												className="btn btn-sm btn-success"
												disabled={!ptoStartDate || !ptoEndDate || ptoLoading || (ptoStartDate > ptoEndDate)}
												onClick={async () => {
													if (!user?.id || !ptoStartDate || !ptoEndDate || ptoStartDate > ptoEndDate) return;
													setPtoLoading(true);
													setMessage(null);
													try {
														const added = await addTimeOff(user.id, {
															start_date: ptoStartDate,
															end_date: ptoEndDate,
															label: ptoLabel.trim() || null,
														});
														setTimeOffEntries((prev) => [...prev, added].sort((a, b) => a.start_date.localeCompare(b.start_date)));
														setPtoStartDate('');
														setPtoEndDate('');
														setPtoLabel('');
														setMessage({ type: 'success', text: 'Time off added.' });
													} catch (e: any) {
														setMessage({ type: 'error', text: e.message || 'Failed to add' });
													} finally {
														setPtoLoading(false);
													}
												}}
											>
												{copy('Add', 'Hinzufügen', 'Dodaj')}
											</button>
										</div>
									</div>
								)}
								{user.role === 'gardener' && (
									<>
										{companyData?.pendingInvites && companyData.pendingInvites.length > 0 && (
											<div className="mb-3">
												<label className="form-label small fw-semibold">Pending invites</label>
												<ul className="list-group list-group-flush">
													{companyData.pendingInvites.map((inv) => (
														<li key={inv.id} className="list-group-item d-flex justify-content-between align-items-center px-0">
															<span>{inv.company_name}</span>
															<div className="d-flex gap-1">
																<button
																	type="button"
																	className="btn btn-sm btn-success"
																	disabled={companyActionLoading}
																	onClick={async () => {
																		if (!user?.id) return;
																		setCompanyActionLoading(true);
																		setMessage(null);
																		try {
																			await acceptCompanyInvite(user.id, inv.id);
																			await refreshUser(user.id);
																			const [data, ctxRes] = await Promise.all([
																				getMyCompany(user.id),
																				getMyCompanyContexts(user.id),
																			]);
																			setCompanyData(data);
																			setMembershipContexts(ctxRes);
																			setMessage({ type: 'success', text: 'You joined the company.' });
																		} catch (e: any) {
																			setMessage({ type: 'error', text: e.message || 'Failed' });
																		} finally {
																			setCompanyActionLoading(false);
																		}
																	}}
																>
																	Accept
																</button>
																<button
																	type="button"
																	className="btn btn-sm btn-outline-secondary"
																	disabled={companyActionLoading}
																	onClick={async () => {
																		if (!user?.id) return;
																		setCompanyActionLoading(true);
																		try {
																			await declineCompanyInvite(user.id, inv.id);
																			const data = await getMyCompany(
																				user.id,
																				user.activeCompanyId ?? undefined
																			);
																			setCompanyData(data);
																			setMessage({ type: 'success', text: 'Invite declined.' });
																		} catch (e: any) {
																			setMessage({ type: 'error', text: e.message || 'Failed' });
																		} finally {
																			setCompanyActionLoading(false);
																		}
																	}}
																>
																	Decline
																</button>
															</div>
														</li>
													))}
												</ul>
											</div>
										)}
										{!companyData?.company && (
											<div className="mb-3">
												<label className="form-label small fw-semibold">Request to join a company</label>
												<div className="d-flex gap-2 flex-wrap">
													<select
														className="form-select form-select-sm"
														style={{ maxWidth: '220px' }}
														value={selectedCompanyId}
														onChange={(e) => setSelectedCompanyId(e.target.value === '' ? '' : Number(e.target.value))}
														disabled={companyActionLoading}
													>
														<option value="">Select company…</option>
														{companiesList.map((c) => (
															<option key={c.id} value={c.id}>{c.name}</option>
														))}
													</select>
													<button
														type="button"
														className="btn btn-sm btn-success"
														disabled={selectedCompanyId === '' || companyActionLoading}
														onClick={async () => {
															if (!user?.id || selectedCompanyId === '') return;
															setCompanyActionLoading(true);
															setMessage(null);
															try {
																await requestJoinCompany(user.id, Number(selectedCompanyId));
																setSelectedCompanyId('');
																const data = await getMyCompany(user.id);
																setCompanyData(data);
																setMessage({ type: 'success', text: 'Request sent. The company will review it.' });
															} catch (e: any) {
																setMessage({ type: 'error', text: e.message || 'Failed to send request' });
															} finally {
																setCompanyActionLoading(false);
															}
														}}
													>
														Request to join
													</button>
												</div>
											</div>
										)}
										{companyData?.company && (
											<div>
												<button
													type="button"
													className="btn btn-sm btn-outline-danger"
													disabled={companyActionLoading}
													onClick={async () => {
														if (!user?.id || !confirm('Leave this company? You can request to join again later.')) return;
														setCompanyActionLoading(true);
														setMessage(null);
														try {
															await leaveCompany(user.id, companyData.company?.id);
															await refreshUser(user.id);
															const [data, ctxRes] = await Promise.all([
																getMyCompany(user.id),
																getMyCompanyContexts(user.id),
															]);
															setCompanyData(data);
															setMembershipContexts(ctxRes);
															setMessage({ type: 'success', text: 'You left the company.' });
														} catch (e: any) {
															setMessage({ type: 'error', text: e.message || 'Failed' });
														} finally {
															setCompanyActionLoading(false);
														}
													}}
												>
													{copy('Leave company', 'Unternehmen verlassen', 'Napusti tvrtku')}
												</button>
											</div>
										)}
									</>
								)}

								{/* Skills section — same column, below Company */}
								<hr className="my-4" />
								<div data-section-id="skills">
									<h5 className="card-title mb-2">{copy('Skills', 'Fähigkeiten', 'Vještine')}</h5>
									<p className="text-muted small mb-3">{copy('Select your gardening skills. These appear when making offers. Saved with Profile.', 'Wählen Sie Ihre Gartenbau-Fähigkeiten. Diese erscheinen beim Erstellen von Angeboten. Wird im Profil gespeichert.', 'Odaberite svoje vrtlarstvo vještine. One se prikazuju pri izradi ponuda. Sprema se u profil.')}</p>
									<div className="row g-3">
										{SKILLS_WITH_DESCRIPTIONS.map(({ key, label, description }) => {
											const isSelected = skills.includes(key);
											const localizedLabel = getSkillLabel(key, label);
											const localizedDescription = getSkillDescription(key, description);
											return (
												<div className="col-12 col-md-6" key={key}>
													<div
														role="button"
														tabIndex={0}
														className={`card h-100 ${isSelected ? 'border-success border-2 shadow-sm' : ''}`}
														onClick={() => {
															if (isSelected) setSkills(skills.filter(s => s !== key));
															else setSkills([...skills, key]);
														}}
														onKeyDown={(e) => {
															if (e.key === 'Enter' || e.key === ' ') {
																e.preventDefault();
																if (isSelected) setSkills(skills.filter(s => s !== key));
																else setSkills([...skills, key]);
															}
														}}
														style={{ cursor: 'pointer', transition: 'all 0.2s' }}
													>
														<div className="card-body d-flex align-items-start">
															<div className="flex-grow-1 text-break">
																<div className="fw-bold">{localizedLabel}</div>
																<p className="text-muted small mb-0 mt-1">{localizedDescription}</p>
															</div>
															{isSelected && (
																<i className="bi bi-check-circle-fill text-success ms-2" style={{ fontSize: '1.25rem' }} aria-hidden />
															)}
														</div>
													</div>
												</div>
											);
										})}
									</div>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>

			{/* Row 2: Preferences — full width at bottom */}
			<div className="row g-4 mt-0">
				<div className="col-12">
					<div className="card" data-section-id="preferences">
						<div className="card-body">
							<h5 className="card-title mb-3">{copy('Preferences', 'Einstellungen', 'Preference')}</h5>
							<div className="list-group list-group-flush">
								<label 
									className="list-group-item d-flex align-items-center justify-content-between"
									data-section-id="email-notifications"
								>
									<div>
										<div className="fw-semibold">{copy('Email notifications', 'E-Mail-Benachrichtigungen', 'Obavijesti e-poštom')}</div>
										<div className="text-muted small">{copy('Get updates about new plan offers and comments.', 'Erhalten Sie Updates zu neuen Angeboten und Kommentaren.', 'Primajte novosti o novim ponudama i komentarima.')}</div>
									</div>
									<input
										className="form-check-input ms-3"
										type="checkbox"
										checked={emailNotifications}
										onChange={(event) => setEmailNotifications(event.target.checked)}
									/>
								</label>
								<label 
									className="list-group-item d-flex align-items-center justify-content-between"
									data-section-id="sms-alerts"
								>
									<div>
										<div className="fw-semibold">{copy('SMS alerts', 'SMS-Benachrichtigungen', 'SMS upozorenja')}</div>
										<div className="text-muted small">{copy('Receive time-sensitive alerts on your phone.', 'Erhalten Sie zeitkritische Benachrichtigungen auf Ihr Telefon.', 'Primajte vremenski osjetljiva upozorenja na mobitel.')}</div>
									</div>
									<input
										className="form-check-input ms-3"
										type="checkbox"
										checked={smsNotifications}
										onChange={(event) => setSmsNotifications(event.target.checked)}
									/>
								</label>
								<label 
									className="list-group-item d-flex align-items-center justify-content-between"
									data-section-id="dark-mode"
								>
									<div>
										<div className="fw-semibold">{copy('Dark mode', 'Dunkelmodus', 'Tamni način')}</div>
										<div className="text-muted small">{copy('Switch to a darker palette for low-light environments.', 'Wechseln Sie zu einer dunkleren Darstellung für Umgebungen mit wenig Licht.', 'Prebacite na tamniju paletu za slabije osvjetljenje.')}</div>
									</div>
									<input
										className="form-check-input ms-3"
										type="checkbox"
										checked={darkMode}
										onChange={(event) => setDarkMode(event.target.checked)}
									/>
								</label>
							</div>
							<div className="d-flex justify-content-end mt-3">
								<button 
									type="button" 
									className="btn btn-success"
									onClick={handleSaveSettings}
									disabled={saving}
								>
									{saving ? copy('Saving...', 'Speichern...', 'Spremanje...') : copy('Save Settings', 'Einstellungen speichern', 'Spremi postavke')}
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
			
			{/* Subscription/Billing Section */}
			<div className="row g-4 mt-2">
				<div className="col-12">
					<div className="card" data-section-id="subscription">
						<div className="card-body">
							<div className="d-flex justify-content-between align-items-center mb-4">
								<h5 className="card-title mb-0">{copy('Subscription & Billing', 'Abonnement & Abrechnung', 'Pretplata i naplata')}</h5>
								<span className="badge text-bg-warning">{copy('Beta', 'Beta', 'Beta')}</span>
							</div>
							<div className="d-flex align-items-center gap-2 mb-2" role="status">
								<span className="badge text-bg-warning">{copy('Coming soon', 'Demnächst', 'Uskoro')}</span>
							</div>
							<p className="text-muted small mb-0">
								{copy(
									'There is no billing during beta testing.',
									'Während des Beta-Tests gibt es keine Abrechnung.',
									'Tijekom beta testiranja nema naplate.'
								)}
							</p>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}


